import type { AirportData, RealismOptions } from '@atc/core';
import { executeInstruction } from './pilot';
/** Per-aircraft memory. Decisions and factual replies never depend on model availability. */
import type { Instruction } from './commands';
import type { Aircraft } from './aircraft';
import { spokenDigits } from './airlines';
import { altitudeSpeech, altitudeText, headingSpeech, headingText, runwaySpeech, speedSpeech, type Phrase } from './comms';
import { INTENTION_TEXT, NATURE_TEXT } from './emergency';
import { validateClearance } from './clearance';
import type { PilotRequest } from './patience';

export const QUERY_SUBJECTS = ['instructions', 'heading', 'altitude', 'speed', 'assignedHeading', 'assignedAltitude', 'assignedSpeed', 'landing', 'intentions', 'personsOnBoard', 'fuelRemaining'] as const;
export type QuerySubject = typeof QUERY_SUBJECTS[number];
export const CONVERSATION_TOPICS = ['greeting', 'capability', 'status', 'reason', 'operational', 'instruction', 'other'] as const;
export type ConversationTopic = typeof CONVERSATION_TOPICS[number];

/** What a crew has to ask for before it can act on something that sounded like an instruction. */
export type Clarification = 'speed' | 'altitude' | 'heading' | 'instruction';
const SPEED_WORDS = /\b(?:reduce|increase|slow(?:er)?|speed|faster|knots)\b/i;
const ALTITUDE_WORDS = /\b(?:climb|descend|descent|altitude|level|flight level|higher|lower)\b/i;
const HEADING_WORDS = /\b(?:turn|heading|left|right)\b/i;
const ORDER_WORDS = /^(?:please |and |now )*(?:clear|vacate|expedite|go|follow|continue|join|proceed|hold|line|cleared|contact|land|taxi|exit|stop|keep|make|do|give|fly|intercept|report|reduce|increase|slow|climb|descend|turn|maintain)\b/i;

/**
 * Conversation that was really an order the sim could not execute: a speed, level or heading
 * with no value ("please reduce speed"), or an action it does not model. A real crew never
 * answers that with "roger"; it asks for the missing part, and no brain may pretend to comply.
 */
export function clarificationFor(topic: ConversationTopic, text: string): Clarification | null {
  if (!['operational', 'instruction', 'other'].includes(topic)) return null;
  const t = text.trim();
  // A value was said, so the crew heard the number but not a command it can fly: ask for the instruction.
  if (!/\d/.test(t)) {
    if (SPEED_WORDS.test(t) && !/\bsay (?:speed|airspeed)\b/i.test(t)) return 'speed';
    if (ALTITUDE_WORDS.test(t) && /\b(?:climb|descend|descent|higher|lower|level)\b/i.test(t)) return 'altitude';
    if (/\bturn\b|\bheading\b/i.test(t) && HEADING_WORDS.test(t)) return 'heading';
  }
  if (topic === 'instruction' || ORDER_WORDS.test(t)) return 'instruction';
  return null;
}
export type PilotMessage = { kind: 'instruction'; instruction: Instruction } | { kind: 'query'; subject: QuerySubject } | { kind: 'conversation'; topic: ConversationTopic; text: string } | { kind: 'standby' | 'repeat' | 'acknowledge' | 'towerTransfer' };
export interface PilotTransmission { callsign: string; messages: PilotMessage[] }
export interface PilotMemory {
  /** Sim time the crew last broke off towards a hold because it was nearing the field uncleared. */
  unclearedS?: number;
  /** Recovery remains active after the kinematic goAround phase ends, until new routing. */
  recovery?: 'goAround';
  heard: Instruction[];
  pending: Instruction[];
  request?: PilotRequest;
  history: { from: 'atc' | 'pilot'; text: string; speech: string; timeS: number }[];
}
export const newPilotMemory = (): PilotMemory => ({ heard: [], pending: [], history: [] });

export function validateTransmission(raw: unknown): PilotTransmission | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (Object.keys(r).some((k) => !['callsign', 'messages'].includes(k)) || typeof r.callsign !== 'string' || !/^[A-Z0-9]{2,8}$/.test(r.callsign) || !Array.isArray(r.messages) || !r.messages.length || r.messages.length > 8) return null;
  const messages: PilotMessage[] = [];
  for (const item of r.messages) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    if (item.kind === 'instruction' && Object.keys(item).every((k) => ['kind', 'instruction'].includes(k))) {
      const p = validateClearance({ callsign: r.callsign, instructions: [item.instruction] });
      if (!p.ok) return null;
      messages.push({ kind: 'instruction', instruction: p.clearance.instructions[0]! });
    } else if (item.kind === 'query' && Object.keys(item).every((k) => ['kind', 'subject'].includes(k)) && QUERY_SUBJECTS.includes(item.subject)) messages.push({ kind: 'query', subject: item.subject });
    else if (item.kind === 'conversation' && Object.keys(item).every((k) => ['kind', 'topic', 'text'].includes(k)) && CONVERSATION_TOPICS.includes(item.topic) && typeof item.text === 'string' && item.text.trim().length > 0 && item.text.length <= 500 && !/[\u0000-\u001f\u007f]/.test(item.text)) messages.push({ kind: 'conversation', topic: item.topic, text: item.text.trim() });
    else if (['standby', 'repeat', 'acknowledge', 'towerTransfer'].includes(item.kind) && Object.keys(item).length === 1) messages.push({ kind: item.kind });
    else return null;
  }
  return { callsign: r.callsign, messages };
}

export function instructionSummary(i: Instruction): Phrase {
  switch (i.kind) {
    case 'heading': return { text: `heading ${headingText(i.headingDeg)}`, speech: `heading ${headingSpeech(i.headingDeg)}` };
    case 'altitude': return { text: `maintain ${altitudeText(i.altitudeFt)}`, speech: `maintain ${altitudeSpeech(i.altitudeFt)}` };
    case 'speed': return { text: `speed ${i.speedKt}`, speech: `speed ${speedSpeech(i.speedKt)}` };
    case 'land': return { text: `cleared to land runway ${i.runway}`, speech: `cleared to land runway ${runwaySpeech(i.runway)}` };
    case 'approach': return { text: `ILS approach runway ${i.runway}`, speech: `I L S approach runway ${runwaySpeech(i.runway)}` };
    case 'direct': return { text: `direct ${i.fix}`, speech: `direct ${i.fix}` };
    case 'hold': return { text: `holding at ${i.fix}`, speech: `holding at ${i.fix}` };
    case 'abort': return { text: 'going around or stopping takeoff', speech: 'going around or stopping takeoff' };
    case 'takeoff': return { text: 'cleared for takeoff', speech: 'cleared for takeoff' };
    case 'lineUp': return { text: 'line up and wait', speech: 'line up and wait' };
    case 'resumeNav': return { text: 'own navigation', speech: 'own navigation' };
    case 'contact': return { text: 'contact departure', speech: 'contact departure' };
  }
}
const join = (parts: Phrase[]): Phrase => ({ text: parts.map((p) => p.text).join(', '), speech: parts.map((p) => p.speech).join(', ') });
const plain = (text: string): Phrase => ({ text, speech: text });

export function pilotAnswer(ac: Aircraft, subject: QuerySubject, airport: AirportData, opts: RealismOptions): Phrase {
  // Replay acknowledged, unexecuted instructions on a detached copy for clearance queries.
  // Measured heading/altitude/speed always come from the real aircraft.
  if (!['heading', 'altitude', 'speed'].includes(subject) && ac.pilot.pending.length) {
    ac = structuredClone(ac);
    const queued = ac.pilot.pending;
    const context = { altitudeInTransmission: queued.some((i) => i.kind === 'altitude'), speedInTransmission: queued.some((i) => i.kind === 'speed') };
    for (const i of queued) executeInstruction(ac, i, airport, opts, context);
    ac.pilot.pending = [];
  }
  const assigned = (kind: 'heading' | 'altitude' | 'speed'): Phrase => {
    const queued = [...ac.pilot.pending].reverse().find((i) => i.kind === kind);
    if (queued) return instructionSummary(queued);
    if (kind === 'heading' && ac.assigned.headingDeg !== undefined) return instructionSummary({ kind, headingDeg: ac.assigned.headingDeg });
    if (kind === 'altitude' && ac.assigned.altitudeFt !== undefined) return instructionSummary({ kind, altitudeFt: ac.assigned.altitudeFt, expedite: !!ac.assigned.expedite });
    if (kind === 'speed' && ac.assigned.speedKt !== undefined) return instructionSummary({ kind, speedKt: ac.assigned.speedKt });
    return plain(`no assigned ${kind}`);
  };
  switch (subject) {
    case 'heading': return instructionSummary({ kind: 'heading', headingDeg: Math.round(ac.headingDeg) || 360 });
    case 'altitude': return { text: `altitude ${altitudeText(Math.round(ac.altitudeFt / 100) * 100)}`, speech: `altitude ${altitudeSpeech(Math.round(ac.altitudeFt / 100) * 100)}` };
    case 'speed': return instructionSummary({ kind: 'speed', speedKt: Math.round(ac.iasKt) });
    case 'assignedHeading': return assigned('heading');
    case 'assignedAltitude': return assigned('altitude');
    case 'assignedSpeed': return assigned('speed');
    case 'landing': {
      const queued = [...ac.pilot.pending].reverse().find((i) => i.kind === 'land');
      if (queued) return instructionSummary(queued);
      return ac.approach?.cleared ? instructionSummary({ kind: 'land', runway: ac.approach.runway.name }) : plain(ac.approach ? 'negative, approach clearance only' : 'negative, no landing clearance');
    }
    case 'intentions': {
      if (ac.emergency) {
        const rw = ac.approach?.runway.name ?? airport.arrivalRunway;
        return { text: `${NATURE_TEXT[ac.emergency.nature]}, ${INTENTION_TEXT[ac.emergency.intention]} ${rw}`, speech: `${NATURE_TEXT[ac.emergency.nature]}, ${INTENTION_TEXT[ac.emergency.intention]} ${runwaySpeech(rw)}` };
      }
      return plain(ac.pilot.recovery === 'goAround' ? 'going around, request vectors' : ac.approach ? 'continue approach' : ac.nav.mode === 'hold' ? 'continue holding' : ac.phase === 'holdShort' ? 'holding short, ready for departure' : 'continue assigned clearance');
    }
    case 'personsOnBoard': {
      const persons = ac.emergency?.persons ?? ac.persons;
      return persons === undefined ? plain('stand by for souls on board') : { text: `${persons} souls on board`, speech: `${spokenDigits(persons)} souls on board` };
    }
    case 'fuelRemaining': {
      if (!ac.fuel) return plain('fuel is not a factor');
      const minutes = Math.max(0, Math.round(ac.fuel.enduranceMin));
      const state = ac.fuel.state === 'mayday' ? ', we are in a fuel emergency' : ac.fuel.state === 'minimum' ? ', minimum fuel' : '';
      return { text: `${minutes} minutes of fuel remaining${state}`, speech: `${spokenDigits(minutes)} minutes of fuel remaining${state}` };
    }
    case 'instructions': {
      const parts: Phrase[] = [];
      const lateral = [...ac.pilot.pending].reverse().find((i) => ['heading', 'direct', 'hold', 'resumeNav', 'approach', 'land'].includes(i.kind));
      if (lateral && lateral.kind !== 'heading') parts.push(instructionSummary(lateral));
      else if (!lateral && ac.nav.mode === 'direct' && ac.nav.fix) parts.push(instructionSummary({ kind: 'direct', fix: ac.nav.fix }));
      else if (ac.nav.mode === 'hold' && ac.nav.hold) parts.push(instructionSummary({ kind: 'hold', fix: ac.nav.hold.fix }));
      else if (ac.nav.mode === 'route') parts.push(plain('following filed route'));
      for (const kind of ['heading', 'altitude', 'speed'] as const) {
        if (kind === 'heading' && lateral && ['direct', 'hold', 'resumeNav'].includes(lateral.kind)) continue;
        const p = assigned(kind); if (!p.text.startsWith('no assigned')) parts.push(p);
      }
      if (ac.approach && !lateral) parts.push(instructionSummary({ kind: ac.approach.cleared ? 'land' : 'approach', runway: ac.approach.runway.name }));
      if (ac.pilot.recovery === 'goAround' || ['holdShort', 'lineUp', 'linedUp', 'takeoffRoll', 'abortRoll', 'goAround'].includes(ac.phase)) parts.unshift(plain(ac.pilot.recovery === 'goAround' || ac.phase === 'goAround' ? 'going around' : ac.phase === 'abortRoll' ? 'aborting takeoff' : ac.phase === 'takeoffRoll' ? 'taking off' : ac.phase === 'holdShort' ? 'holding short' : 'line up and wait'));
      return parts.length ? join(parts) : plain('no assigned clearance');
    }
  }
}
